import React, { useState, useEffect, useCallback } from "react";
import styles from "./vocabularyModal.module.css";
import axios from "axios";
import { addNewLearningTool } from "../../../utilities/learningZonesUtils";

const apiUrl = process.env.REACT_APP_URL;
axios.defaults.baseURL = `${apiUrl}`;
axios.defaults.withCredentials = true;

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const VocabularyModal = ({
  header,
  unitContent,
  learningToolId,
  learningZoneId,
  approveBtnText,
  cancelBtnText,
  show,
  onClose,
  onConfirm,
}) => {
  /* The vocabulary modal is used to enter details of a new vocabulary unit, as well as to edit an existing vocabulary unit. */

  const [origText, setOrigText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [resourceFile, setResourceFile] = useState(null);
  const [resourceUrl, setResourceUrl] = useState("");
  const [audioOption, setAudioOption] = useState("keep");
  const [isValid, setIsValid] = useState(false);
  const [fileError, setFileError] = useState("");
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (unitContent && show) {
      setOrigText(unitContent.vocab_orig_text);
      setTranslatedText(unitContent.vocab_translated_text);
      setResourceUrl(unitContent.resource_url);
      if (unitContent.resource_url) {
        setAudioOption("keep");
      } else {
        setAudioOption("upload");
      }
    } else {
      setOrigText("");
      setTranslatedText("");
      setResourceUrl("");
      setResourceFile(null);
      setAudioOption("upload");
    }
  }, [unitContent, show]);

  /**
   * Validate the content of a new / updated vocabulary unit
   */
  const validateForm = useCallback(() => {
    let newErrorMsg = "";

    if (origText.trim() === "") {
      setIsValid(false);
      newErrorMsg += "Please enter the original text. ";
    }

    if (translatedText.trim() === "") {
      setIsValid(false);
      newErrorMsg += "Please enter the translated text.";
    }

    if (newErrorMsg) {
      setError(newErrorMsg);
    } else {
      setError("");
      setIsValid(true);
    }
  }, [origText, translatedText]);

  useEffect(() => {
    validateForm();
  }, [origText, translatedText, validateForm]);

  /**
   * Handle change of the original text of a unit
   */
  const handleOrigTextChange = (e) => {
    setOrigText(e.target.value);
  };

  /**
   * Handle change of the translated text of a unit
   */
  const handleTranslatedTextChange = (e) => {
    setTranslatedText(e.target.value);
  };

  /**
   * Handle change of the audio file of a unit.
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError("File size exceeds 2MB. Please upload a smaller file.");
        setResourceFile(null);
      } else if (!file.type.startsWith("audio/")) {
        setFileError("Invalid file type. Please upload a valid audio file.");
        setResourceFile(null);
      } else {
        setResourceFile(file);
        setFileError("");
      }
    }
  };

  /**
   * Add a new vocabulary unit to the tool.
   */
  const handleAddNewUnit = async () => {
    validateForm();
    setShowError(true);

    if (!isValid) {
      return;
    }

    let toolId = learningToolId;

    if (!toolId) {
      /* This is the first unit of the tool, so it should be created first. */
      try {
        toolId = await addNewLearningTool(learningZoneId, "vocabulary");
      } catch (error) {
        console.error(error.message);
        return;
      }
    }

    const newUnitContent = {
      learningToolId: toolId,
      vocabOrigText: origText,
      vocabTranslatedText: translatedText,
    };

    try {
      const reqUrl = `/learning_units/create/vocabulary`;

      const response = await axios.post(reqUrl, {
        learningToolId: toolId,
        vocabOrigText: newUnitContent.vocabOrigText,
        vocabTranslatedText: newUnitContent.vocabTranslatedText,
      });

      if (response.status === 200 || response.status === 201) {
        newUnitContent.learning_unit_id = response.data.learning_unit_id;

        if (resourceFile) {
          const formData = new FormData();
          formData.append("audio", resourceFile);
          formData.append("learningToolId", toolId);

          const audioResponse = await axios.put(
            `/learning_units/add_audio/vocabulary/${newUnitContent.learning_unit_id}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (audioResponse.status === 200 && audioResponse.data.success) {
            newUnitContent.resource_url = audioResponse.data.audio_url;
          } else {
            console.error("Error - audio was not uploaded.");
          }
        }

        onConfirm({
          learning_tool_id: toolId,
          tool_type_id: "vocabulary",
          newUnit: {
            learning_unit_id: newUnitContent.learning_unit_id,
            vocab_orig_text: newUnitContent.vocabOrigText,
            vocab_translated_text: newUnitContent.vocabTranslatedText,
            resource_url: newUnitContent.resource_url || "",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_active: 1,
            learning_tool_id: toolId,
            unit_type: "vocabulary",
            unitSolutions: [],
          },
        });

        setError("");
        setShowError(false);
      } else {
        console.error("Failed to add the new learning unit.");
      }
    } catch (error) {
      console.error("Failed to add the new learning unit.");
    }

    onClose();
  };

  /**
   * Update a unit in the database and on the page display
   */
  const handleConfirm = async () => {
    validateForm();
    setShowError(true);

    if (!isValid) {
      return;
    }

    const formData = new FormData();
    formData.append("vocab_orig_text", origText);
    formData.append("vocab_translated_text", translatedText);
    if (audioOption === "upload" && resourceFile) {
      formData.append("audio", resourceFile);
    } else {
      formData.append("resource_url", resourceUrl);
    }

    try {
      const res = await axios.put(
        `/learning_units/update/vocabulary/${unitContent.learning_tool_id}/${unitContent.learning_unit_id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.status === 200 && res.data.success) {
        onConfirm({
          vocab_orig_text: origText,
          vocab_translated_text: translatedText,
          resource_url: res.data.resource_url || resourceUrl,
        });
      } else {
        console.error("Error - unit was not updated.");
      }
    } catch (error) {
      console.error("Error - unit was not updated.", error);
    }

    onClose();
  };

  /**
   * Reset the modal text and file fields, and the error message
   */
  const resetState = () => {
    if (unitContent) {
      setOrigText(unitContent.vocab_orig_text);
      setTranslatedText(unitContent.vocab_translated_text);
      setResourceUrl(unitContent.resource_url);
      setResourceFile(null);
      setFileError("");
      if (unitContent.resource_url) {
        setAudioOption("keep");
      } else {
        setAudioOption("upload");
      }
    } else {
      setOrigText("");
      setTranslatedText("");
      setResourceUrl("");
      setResourceFile(null);
      setAudioOption("upload");
      setFileError("");
      setError("");
      setShowError(false);
    }
  };

  /**
   * Close the modal when clicking outside it
   * @param {*} e
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      resetState();
      onClose();
    }
  };

  /**
   * Close the modal
   */
  const handleClose = () => {
    resetState();
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={handleClose}>
          &times;
        </button>
        <h2>{header}</h2>
        <div className={styles.field}>
          <label>Original Text</label>
          <input type="text" value={origText} onChange={handleOrigTextChange} />
        </div>
        <div className={styles.field}>
          <label>Translated Text</label>
          <input
            type="text"
            value={translatedText}
            onChange={handleTranslatedTextChange}
          />
        </div>
        {resourceUrl ? (
          <div className={styles.field}>
            <label>Audio File Option</label>
            <div className={styles.radioGroup}>
              <input
                type="radio"
                id="keep"
                name="audioOption"
                value="keep"
                checked={audioOption === "keep"}
                onChange={() => setAudioOption("keep")}
              />
              <label htmlFor="keep">Keep Current Audio</label>
            </div>
            <div className={styles.radioGroup}>
              <input
                type="radio"
                id="upload"
                name="audioOption"
                value="upload"
                checked={audioOption === "upload"}
                onChange={() => setAudioOption("upload")}
              />
              <label htmlFor="upload">Upload New Audio</label>
            </div>
          </div>
        ) : null}
        {audioOption === "upload" && (
          <div className={styles.field}>
            <label>Resource File</label>
            <input type="file" accept="audio/*" onChange={handleFileChange} />
            {fileError && <div className={styles.error}>{fileError}</div>}
          </div>
        )}
        <p
          className={`${styles.error} ${
            !isValid && showError ? styles.visible : ""
          }`}
        >
          {error}
        </p>
        <div className={styles.actions}>
          <button onClick={handleClose}>{cancelBtnText}</button>
          {!unitContent && (
            <button onClick={handleAddNewUnit}>{approveBtnText}</button>
          )}
          {unitContent && (
            <button onClick={handleConfirm} disabled={!isValid}>
              {approveBtnText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VocabularyModal;
